import React from 'react';
import { Transition } from 'react-transition-group';


export type CollapseWidthProps = {
  in: boolean;
  timeout?: number;
  children: React.ReactElement<{ style: React.CSSProperties }>;
  onExited?: () => void;
  width?: string;
};


export const CollapseWidth: React.VFC<CollapseWidthProps> = ({ in: inProp, timeout = 300, width, children, onExited }) => {
  const elRef = React.useRef<HTMLDivElement | null>(null);
  const defaultStyle: React.CSSProperties = React.useMemo(() => ({
    transitionDelay: '0ms',
    transitionDuration: `${timeout}ms`,
  }), [ timeout ]);

  const _width = React.useMemo(() => {
    if (width) {
      return width;
    }

    if (elRef.current) {
      const widths = Array.from(elRef.current.children).map(c => c?.getBoundingClientRect()?.width);
      const max = Math.max(...widths);

      return max;
    }

    return width;
  }, [ inProp, width ]); // eslint-disable-line react-hooks/exhaustive-deps

  const transitionStyles = React.useMemo(() => {
    return {
      entering: { width: _width, minWidth: _width },
      entered:  { width: _width, minWidth: _width },
      exiting:  { width: 0, minWidth: 0 },
      exited:   { width: 0, minWidth: 0 },
      unmounted: null,
    };
  }, [ _width ]);

  return (
    <Transition in={inProp} timeout={timeout} onExited={onExited} appear>
      {(state) => (
        <div
          ref={elRef}
          data-step={state}
          style={{
            overflowX: 'hidden',
            ...defaultStyle,
            ...transitionStyles[state]
          }}
        >
          {children}
        </div>
      )}
    </Transition>
  );
};

